// Tiny inline formatters for source-list columns. Inlined here
// instead of pulling a date library — these are the only places we
// need them today, and the use cases are narrow (Swedish UI, short
// labels). When the app eventually grows other formatted-date /
// formatted-size needs, lift these into src/lib/format/.

// Bytes → short human label. Returns "—" for null/undefined size.
// Backend's size_bytes is null for URL sources, which we render as
// the em-dash rather than "0 B".
export function formatBytes(n: number | null | undefined): string {
    if (n == null) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// ISO date → relative Swedish label ("just nu" / "5 min sedan" /
// "3 timmar sedan" / "i går" / "5 dagar sedan" / "23 mar"). Anything
// over ~14 days falls back to an absolute date.
//
// Parses a Date from the iso string; returns "—" on parse failure
// so the column never renders the literal "Invalid Date".
export function formatRelativeDate(iso: string | null | undefined, now: Date = new Date()): string {
    if (!iso) return '—';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return '—';
    const diffMs = now.getTime() - then.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'just nu';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min sedan`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} ${hr === 1 ? 'timme' : 'timmar'} sedan`;
    const day = Math.floor(hr / 24);
    if (day === 1) return 'i går';
    if (day < 14) return `${day} dagar sedan`;
    // Absolute fallback: "23 mar" or "23 mar 2025" if a different year.
    const months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
    const sameYear = then.getFullYear() === now.getFullYear();
    const base = `${then.getDate()} ${months[then.getMonth()]}`;
    return sameYear ? base : `${base} ${then.getFullYear()}`;
}
